import React from 'react';
import sha1 from 'js-sha1';
import { getCohortFieldAndPeriod } from './metricsUtils';
import { SERVER_DEFAULT_ICON_URL } from '../utils/apps';
import css from './page.css';
import { FormattedCreativeTypes } from './types';

const SHA_PREFIX = '1290726c5000cab87381bcd9f7b3f39a95586c32';
const CDN_URL_PREFIX = 'https://cdn.singular.net/dashboard/creatives/';
export const UNKNOWN_INDICATION = 'N/A';
export const DEFAULT_ICON_SRC = '/static/dashboard_react/src/resources/icons/default_app_icon.svg';

export const MULTI_VALUED_FIELDS = [
    {
        originalField: 'os',
        aggregatedField: 'osList',
    },
    {
        originalField: 'asset_name',
        aggregatedField: 'assetNameList',
    },
    {
        originalField: 'creative_name',
        aggregatedField: 'creativeNameList',
    },
    {
        originalField: 'asset_id',
        aggregatedField: 'assetIdList',
    },
    {
        originalField: 'creative_id',
        aggregatedField: 'creativeIdList',
    },
    {
        originalField: 'creative_video_duration',
        aggregatedField: 'creativeVideoDurationList',
    },
    {
        originalField: 'unified_campaign_name',
        aggregatedField: 'campaignNameList',
        showMultiValuedTag: true,
    },
    {
        originalField: 'country_field',
        aggregatedField: 'countryList',
        group: 'metadata_dimensions',
        showMultiValuedTag: true,
    },
];

export const MULTI_VALUED_FIELDS_BY_GROUP = MULTI_VALUED_FIELDS.reduce((acc, { originalField, group }) => {
    const dimensionsGroup = group || 'dimensions';
    acc[dimensionsGroup] = acc[dimensionsGroup] || [];
    acc[dimensionsGroup].push(originalField);
    return acc;
}, {});

const getItemIcon = url => {
    const src = !url || url === SERVER_DEFAULT_ICON_URL ? DEFAULT_ICON_SRC : url;
    return <img className={css.autocompleteOptionIcon} src={src} alt={src} />;
};

export const convertObjectToOption = (item, iconField) => ({
    value: item.name,
    label: item.displayName,
    icon: getItemIcon(item[iconField]),
});

export const getOptionsList = (items, iconField) => items.map(item => convertObjectToOption(item, iconField));

export const convertHashToCdnUrl = creativeHash => {
    const hash = sha1(`${SHA_PREFIX}${creativeHash}`);
    return `${CDN_URL_PREFIX}${hash}`;
};

const excludeUnknownsValues = (aggregatedDimensions, creativeImageHash, fieldName) => {
    aggregatedDimensions[creativeImageHash][fieldName] = [...aggregatedDimensions[creativeImageHash][fieldName]].filter(
        value => value !== UNKNOWN_INDICATION
    );
};

// Grouping the multivalued dimensions by creative
export const getAggregatedDimensionsByCreative = multiValuedDimensions => {
    const aggregatedDimensions = (multiValuedDimensions || []).reduce((acc, creative) => {
        const creativeAggregatedDimensions = acc[creative.creative_image_hash] || {};

        MULTI_VALUED_FIELDS.forEach(({ aggregatedField, originalField }) => {
            const creativeAggregatedDimensionField = creativeAggregatedDimensions[aggregatedField] || new Set();
            creativeAggregatedDimensionField.add(creative[originalField]);
            creativeAggregatedDimensions[aggregatedField] = creativeAggregatedDimensionField;
        });

        acc[creative.creative_image_hash] = creativeAggregatedDimensions;

        return acc;
    }, {});

    Object.keys(aggregatedDimensions).forEach(creativeImageHash => {
        MULTI_VALUED_FIELDS.forEach(({ aggregatedField }) => {
            excludeUnknownsValues(aggregatedDimensions, creativeImageHash, aggregatedField);
        });
    });

    return aggregatedDimensions;
};

const getCreativeFieldValue = (field, creative, isCohort) => {
    if (!isCohort) return creative[field];
    const [cohortField, cohortPeriod] = getCohortFieldAndPeriod(field);
    return cohortPeriod ? creative[cohortField]?.[cohortPeriod] || creative[field] : creative[cohortField];
};

const createCreativeFieldsObject = (fields, creative, isCohort = false) => {
    return fields.reduce((acc, { value }) => {
        acc[value] = getCreativeFieldValue(value, creative, isCohort) || 0;
        return acc;
    }, {});
};

export const hasValidMetrics = (metrics, cohorts, selectedMetrics, selectedCohorts) => {
    return (
        selectedMetrics.some(({ value }) => !!metrics[value]) || selectedCohorts.some(({ value }) => !!cohorts[value])
    );
};

export const formatCreatives = (creatives, selectedMetrics, selectedCohorts, creativesMultiValuedDimensions) => {
    if (!creatives?.length) return [];

    const aggregatedDimensionsByCreative = getAggregatedDimensionsByCreative(creativesMultiValuedDimensions);

    return creatives.reduce((acc, creative) => {
        const metrics = createCreativeFieldsObject(selectedMetrics, creative);
        const cohorts = createCreativeFieldsObject(selectedCohorts, creative, true);

        if (hasValidMetrics(metrics, cohorts, selectedMetrics, selectedCohorts)) {
            const imageHash = creative.creative_image_hash;
            acc.push({
                imageHash,
                type: FormattedCreativeTypes[creative.creative_type],
                embeddingUrl: creative.creative_embedding_url,
                height: parseInt(creative.creative_height, 10),
                width: parseInt(creative.creative_width, 10),
                url: convertHashToCdnUrl(imageHash),
                orientation: creative.creative_orientation,
                videoDurationRange: creative.creative_video_duration_range,
                multiValuedDimensions: aggregatedDimensionsByCreative[imageHash] ?? {},
                metrics,
                cohorts,
            });
        }

        return acc;
    }, []);
};
